import type { AudioCodec, SampleRate } from '@namu/protocol'

/**
 * Opus in the browser, when the browser has it.
 *
 * WebCodecs gives access to the same libopus every browser already ships for
 * WebRTC, without shipping a copy in the bundle. That matters here more than
 * usual: this app loads over metered mobile data, and a WebAssembly codec would
 * be a few hundred kilobytes on every first visit, spent to save bandwidth.
 *
 * Support is real but not universal. Chrome and Android have had it for years;
 * Safari arrived later. So it is negotiated rather than assumed: the protocol
 * lets the client pick its codec in both directions, and a browser without
 * WebCodecs simply asks for raw audio and works, at roughly sixteen times the
 * data. Better an app that costs more to use than one that does not run.
 */

export interface CodecSupport {
  encode: boolean
  decode: boolean
}

/**
 * What this browser can do.
 *
 * Only checks that the classes exist. Actually configuring an encoder is
 * asynchronous and can fail for reasons that have nothing to do with support,
 * so the real proof is that `createOpusEncoder` resolved.
 */
export function detectOpusSupport(): CodecSupport {
  const scope = globalThis as unknown as {
    AudioEncoder?: unknown
    AudioDecoder?: unknown
  }
  return {
    encode: typeof scope.AudioEncoder === 'function',
    decode: typeof scope.AudioDecoder === 'function',
  }
}

/** The codec to ask for, given what this browser can actually do. */
export function preferredCodec(): AudioCodec {
  const support = detectOpusSupport()
  return support.encode && support.decode ? 'opus' : 'pcm16'
}

export interface OpusEncoder {
  /** Takes PCM16 and calls back with whole Opus packets. */
  encode(pcm: Int16Array): void
  close(): void
}

export interface OpusDecoder {
  /** Takes one Opus packet and calls back with PCM16. */
  decode(packet: Uint8Array): void
  close(): void
}

/** Matches the server's frame size, so packets line up with what it sends. */
const FRAME_MS = 20

interface EncoderTypes {
  AudioEncoder: new (init: {
    output: (chunk: { byteLength: number; copyTo(dest: Uint8Array): void }) => void
    error: (error: Error) => void
  }) => {
    configure(config: unknown): void
    encode(data: unknown): void
    close(): void
    state: string
  }
  AudioData: new (init: {
    format: string
    sampleRate: number
    numberOfFrames: number
    numberOfChannels: number
    timestamp: number
    data: ArrayBufferView
  }) => { close(): void }
}

export async function createOpusEncoder(
  sampleRate: SampleRate,
  bitrate: number,
  onPacket: (packet: Uint8Array) => void,
): Promise<OpusEncoder | null> {
  const scope = globalThis as unknown as Partial<EncoderTypes>
  if (!scope.AudioEncoder || !scope.AudioData) return null

  let failed = false
  const encoder = new scope.AudioEncoder({
    output: (chunk) => {
      const bytes = new Uint8Array(chunk.byteLength)
      chunk.copyTo(bytes)
      onPacket(bytes)
    },
    error: () => {
      // Reported once and then ignored. An encoder that has errored is dead,
      // and the alternative to carrying on silently is a conversation that
      // ends because of a codec.
      failed = true
    },
  })

  try {
    encoder.configure({
      codec: 'opus',
      sampleRate,
      numberOfChannels: 1,
      bitrate,
      opus: { frameDuration: FRAME_MS * 1000, application: 'voip' },
    })
  } catch {
    return null
  }

  /*
   * A monotonic timestamp in microseconds, which WebCodecs requires and uses to
   * order frames. It has to advance by exactly one frame each time: derive it
   * from a clock instead and any jitter in when the worklet delivers becomes a
   * timestamp gap, which the encoder reads as a discontinuity.
   */
  let timestamp = 0

  return {
    encode(pcm) {
      if (failed || encoder.state !== 'configured') return
      const data = new scope.AudioData!({
        format: 's16',
        sampleRate,
        numberOfFrames: pcm.length,
        numberOfChannels: 1,
        timestamp,
        data: pcm,
      })
      timestamp += Math.round((pcm.length / sampleRate) * 1_000_000)
      try {
        encoder.encode(data)
      } finally {
        // WebCodecs objects hold memory outside the JavaScript heap and are not
        // garbage collected. Missing this leaks steadily for the length of a
        // conversation.
        data.close()
      }
    },
    close() {
      try {
        if (encoder.state !== 'closed') encoder.close()
      } catch {
        // Already gone.
      }
    },
  }
}

interface DecoderTypes {
  AudioDecoder: new (init: {
    output: (frame: {
      numberOfFrames: number
      close(): void
      copyTo(dest: ArrayBufferView, options: { planeIndex: number; format?: string }): void
    }) => void
    error: (error: Error) => void
  }) => {
    configure(config: unknown): void
    decode(chunk: unknown): void
    close(): void
    state: string
  }
  EncodedAudioChunk: new (init: {
    type: string
    timestamp: number
    data: ArrayBufferView
  }) => unknown
}

export async function createOpusDecoder(
  sampleRate: SampleRate,
  onPcm: (pcm: Float32Array) => void,
): Promise<OpusDecoder | null> {
  const scope = globalThis as unknown as Partial<DecoderTypes>
  if (!scope.AudioDecoder || !scope.EncodedAudioChunk) return null

  let failed = false
  const decoder = new scope.AudioDecoder({
    output: (frame) => {
      const samples = new Float32Array(frame.numberOfFrames)
      try {
        frame.copyTo(samples, { planeIndex: 0, format: 'f32-planar' })
        onPcm(samples)
      } finally {
        frame.close()
      }
    },
    error: () => {
      failed = true
    },
  })

  try {
    decoder.configure({ codec: 'opus', sampleRate, numberOfChannels: 1 })
  } catch {
    return null
  }

  let timestamp = 0

  return {
    decode(packet) {
      if (failed || decoder.state !== 'configured') return
      const chunk = new scope.EncodedAudioChunk!({
        type: 'key',
        timestamp,
        data: packet,
      })
      timestamp += FRAME_MS * 1000
      try {
        decoder.decode(chunk)
      } catch {
        // One packet that will not decode is twenty milliseconds of audio.
        // Dropping it is right; ending the conversation over it is not.
      }
    },
    close() {
      try {
        if (decoder.state !== 'closed') decoder.close()
      } catch {
        // Already gone.
      }
    },
  }
}

/** PCM16 bytes to float samples, for feeding the Web Audio graph. */
export function pcm16ToFloat(bytes: Uint8Array): Float32Array {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const samples = new Float32Array(Math.floor(bytes.byteLength / 2))
  for (let i = 0; i < samples.length; i += 1) {
    samples[i] = view.getInt16(i * 2, true) / 0x8000
  }
  return samples
}
